import type { CaseStatus, EvidenceLevel, QiCase, QiConsent, QiFollowup, QiSessionRecord, RecipientConsentInput } from './types';

export const QUALIFICATION_STATUS_LABELS: Record<string, string> = {
  approved: '正式施炁者（已核准）',
  under_review: '準施炁者（審定中）',
  revoked: '已撤銷',
  not_started: '尚未開始',
};

export const PRACTITIONER_STATUS_LABELS: Record<string, string> = {
  none: '一般會員｜未進入施炁培育',
  candidate: '準施炁者｜實作培育中',
  mentor_review: '準施炁者｜導師覆核中',
  authorized: '正式授權施炁者',
  suspended: '已停權',
};

export const AUTHORIZATION_STATUS_LABELS: Record<string, string> = {
  formally_authorized: '正式授權',
  provisional_authorized: '暫行授權',
  revoked: '已撤銷',
  none: '無',
};

export const OBSERVATION_STATUS_LABELS: Record<string, string> = {
  completed: '已完成',
  active: '觀察中',
  under_observation: '觀察中',
  not_started: '尚未開始',
};

export const SACRED_LIGHT_STATUS_LABELS: Record<string, string> = {
  opened: '已開啟',
  not_opened: '未開啟',
  under_observation: '觀察中',
};

export const SUBFIELD_STATUS_LABELS: Record<string, string> = {
  entered: '已進入',
  preparing: '準備中',
};

export const CASE_STATUS_LABELS: Record<CaseStatus, string> = {
  draft: '草稿',
  submitted: '已送出',
  awaiting_recipient: '等待受施者確認',
  under_review: '覆核中',
  need_more_evidence: '需要補件',
  private_complete: '完成（不公開）',
  approved_anonymous: '已核准匿名公開',
  published: '已發布',
  follow_up_due: '待追蹤',
  withdrawn: '已撤回公開',
  archived: '封存',
  recipient_confirmation_pending: '案主可後續補充',
  recipient_confirmed: '案主已補充確認',
  recipient_disputed: '案主提出異議',
};

export const EVIDENCE_LEVELS: { value: EvidenceLevel; label: string }[] = [
  { value: 'E0', label: 'E0 單一口述或未確認訊息' },
  { value: 'E1', label: 'E1 當事人本人確認' },
  { value: 'E2', label: 'E2 有照片／影片／文件或第三方紀錄' },
  { value: 'E3', label: 'E3 完成 7／30／90 日至少兩次追蹤' },
  { value: 'E4', label: 'E4 外部量測／專業報告／可比對前後資料' },
];

export const FOLLOWUP_LABELS: Record<string, string> = {
  day7: '7 日追蹤',
  day30: '30 日追蹤',
  day90: '90 日追蹤',
};

export const PRACTICE_LOCATIONS = [
  { value: 'taipei', label: '臺北' },
  { value: 'hualien', label: '花蓮' },
  { value: 'other', label: '其他（由管理者設定）' },
] as const;

export const PUBLICATION_WITHDRAWAL_REASONS = [
  { value: 'reidentification_risk', label: '仍可能辨識案主' },
  { value: 'third_party_privacy', label: '涉及第三人隱私' },
  { value: 'unsuitable_content', label: '內容不適合公開' },
  { value: 'medical_claim', label: '涉及不當療效或醫療宣稱' },
  { value: 'disputed_or_inaccurate', label: '資料有爭議或需要更正' },
  { value: 'media_permission_missing', label: '照片／影音授權不足' },
  { value: 'other', label: '其他' },
] as const;

export const OUTCOME_OPTIONS = [
  '明顯改善',
  '部分改善',
  '無明顯改變',
  '變得更不舒服',
  '已無法判斷',
];

// 首次加入可一次授權匿名文字研究與公開；照片、聲音、影片仍逐件授權。
export const RECIPIENT_CONSENT_ITEMS: { key: keyof RecipientConsentInput; label: string }[] = [
  { key: 'storage_consent', label: '同意保存本次施炁紀錄' },
  { key: 'followup_consent', label: '同意系統聯絡本人進行 7／30／90 日追蹤' },
  { key: 'reviewer_access', label: '同意 ABAA 及指定 Reviewer 查看完整資料' },
  { key: 'internal_anonymous_use', label: '同意以去識別化方式用於內部研究與系統改善' },
  { key: 'public_anonymous_use', label: '同意以匿名方式公開為宇炁見證' },
  { key: 'media_public_use', label: '同意公開未露臉、無可辨識資訊的照片或影片' },
];

export const IMMEDIATE_REACTIONS = [
  '無明顯反應',
  '熱',
  '冷',
  '麻',
  '振動',
  '自發性動作',
  '抖動',
  '伸展',
  '呼吸改變',
  '情緒釋放',
  '聲音或語言反應',
  '影像、意象或意識感受',
  '疼痛或不適增加',
  '疼痛或不適減少',
  '其他',
];

export const BODY_PARTS = [
  '頭部',
  '頸部',
  '肩部',
  '背部',
  '腰部',
  '胸部',
  '腹部',
  '骨盆',
  '手臂',
  '手部',
  '腿部',
  '膝蓋',
  '足部',
  '全身',
  '其他',
];

export const CONTACT_METHODS = ['未接觸身體', '輕觸', '其他'];

export const SOURCE_TYPES: { value: string; label: string }[] = [
  { value: 'recipient_self_report', label: '受施者本人自述' },
  { value: 'practitioner_observation', label: '施炁者觀察' },
  { value: 'existing_media', label: '既有照片或文件' },
  { value: 'other', label: '其他' },
];

export const REVIEW_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'in_review', label: '覆核中' },
  { value: 'need_more_evidence', label: '需要補件' },
  { value: 'approved', label: '核准公開' },
  { value: 'rejected', label: '不予公開' },
];

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleString('zh-TW', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Generate a usable anonymous draft when the practitioner did not provide one.
// The reviewer can still edit it before publishing. Known direct identifiers are
// removed; medical/experience details remain because they are the witness itself.
export function buildAnonymousCaseSummary(
  qCase: QiCase,
  records: QiSessionRecord[],
  followups: QiFollowup[],
): string {
  const stageLabels: Record<string, string> = {
    pre_condition: '施炁前',
    method: '施炁過程',
    immediate_reaction: '當下反應',
    post_condition: '施炁後',
  };
  const rawParts = records
    .filter((record) => record.narrative?.trim())
    .map((record) => `${stageLabels[record.record_stage] || '紀錄'}：${record.narrative!.trim()}`);
  const completedFollowups = followups.filter((item) => item.status === 'completed' && item.self_report?.trim());
  completedFollowups.forEach((item) => {
    rawParts.push(`${FOLLOWUP_LABELS[item.followup_type] || '後續追蹤'}：${item.self_report!.trim()}`);
  });

  let summary = rawParts.join('；') || '本案例已完成宇炁實作紀錄，相關內容與後續變化持續保存及追蹤。';
  const directIdentifiers = [qCase.recipient_alias, qCase.recipient_email]
    .filter((value): value is string => Boolean(value?.trim()));
  directIdentifiers.forEach((value) => {
    summary = summary.replaceAll(value, '案主');
  });
  summary = summary
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[Email已隱去]')
    .replace(/(?:\+?886[-\s]?)?0?9\d{2}[-\s]?\d{3}[-\s]?\d{3}/g, '[電話已隱去]')
    .replace(/(?:0\d{1,2}[-\s]?)?\d{6,8}/g, '[電話已隱去]');
  return summary.slice(0, 500);
}

// ── Reviewer 發布資格 ─────────────────────────────

export interface PublishEligibility {
  hasConsentRecord: boolean;
  canPublishText: boolean;
  canPublishMedia: boolean;
  missing: string[];
}

// 匿名文字與無臉資料由施炁者完成去識別確認後即可送 ABAA。
// 案主只有明確撤回公開時才阻擋文字；露臉媒體仍須逐檔取得同意。
export function evaluatePublishEligibility(consent: QiConsent | null): PublishEligibility {
  const withdrawn = Boolean(consent?.withdrawn_at);
  return {
    hasConsentRecord: Boolean(consent),
    canPublishText: !withdrawn,
    canPublishMedia: !withdrawn && consent?.media_public_use === true,
    missing: withdrawn ? ['withdrawn_at'] : [],
  };
}

// 缺項 key → 中文標籤（consent_record／withdrawn_at 為合成缺項）
export const CONSENT_FIELD_LABELS: Record<string, string> = {
  storage_consent: '保存紀錄',
  followup_consent: '追蹤聯絡',
  reviewer_access: 'Reviewer 查看',
  internal_anonymous_use: '去識別化內部使用',
  public_anonymous_use: '匿名公開文字',
  media_public_use: '公開媒體',
  consent_record: '同意紀錄',
  withdrawn_at: '未撤回公開授權',
};
